<template>
    <div class="cb-wizard-step3 cb-wizard__container">
        <div class="cb-wizard__body">
            <div class="cb-wizard__header">
                <h3 class="cb-wizard__title">Title tag and meta description</h3>
                <Stepper class="cb-wizard__stepper"
                    :labels="['Step1', 'Step2', 'Step3', 'Step4', 'Step5']"
                    :currentStep="2" />
            </div>
            <div class="cb-wizard-step3__body">
                <GoogleSerpSimulator class="cb-wizard-step3__serp"
                    :url="url"
                    :full-url="url"
                    :title="titleInner || 'Title placeholder'"
                    :description="descriptionInner || 'Description placeholder'"
                    mode="desktop" />
                <RecommendedContent v-model="titleInner"
                    title="Title"
                    class="cb-wizard-step3__recommended"
                    :max-length="60"
                    :orange-zone="65"
                    :min-length="20"
                    type="title"
                    single-line />
                <RecommendedContent v-model="descriptionInner"
                    class="cb-wizard-step3__recommended"
                    title="Meta Description"
                    type="description"
                    :max-length="160"
                    :orang-zone="170"
                    :min-length="100" />

                <div class="common-table cb-wizard-step3__table">
                    <div class="common-table__row common-table__header">
                        <div v-for="column in tableConfig"
                            class="common-table__header-cell"
                            :class="'cb-wizard-step3__' + column.propName + '-cell'">
                            <base-table-sortable-column class="cb-wizard-step3__sortable-header"
                                :label="column.label"
                                :prop-name="column.propName"
                                :sort-config="sortConfig"
                                @click.native="onHeaderCellClicked(column)" />
                        </div>
                    </div>
                    <div class="cb-wizard-step3__table-body"
                        :ref="'scrollableTablePart'">
                        <div v-for="keywordItem in sortedKeywordsList"
                            class="common-table__row">
                            <div v-for="column in tableConfig"
                                class="common-table__cell"
                                :class="'cb-wizard-step3__' + column.propName + '-cell'">
                                <template v-if="column.propName === 'keyword'">
                                    {{ keywordItem[column.propName] }}
                                </template>
                                <template v-else>
                                    {{ keywordItem[column.propName] | commaDividedNumber }}
                                </template>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="cb-wizard__sidebar">
            <span class="cb-wizard__sidebar-title">Write a Title Tag & Meta Description for your Content</span>
            <div>(you can always update this later!)</div>
            <span class="cb-wizard__sidebar-pro-tip">Pro tip: <base-material-design-icon icon="lightBulbOutline"/></span>
            <p class="cb-wizard__sidebar-text">Put your most important and most specific keyword phrase at or near the beginning of the title tag.</p>
            <p class="cb-wizard__sidebar-text">You can go over the character limit recommendation, however the title tag may not fully appear in search results.</p>
            <p class="cb-wizard__sidebar-text">The Meta Description should be unique and provide a compelling reason for searchers to click!</p>
            <div class="cb-wizard__controls">
                <button class="cb-wizard__button"
                    :disabled="!titleInner || !descriptionInner"
                    @click="next">Next</button>
                <button class="cb-wizard__button cb-wizard__button_inverted"
                    @click="back">Previous</button>
            </div>
        </div>
    </div>
</template>

<script>
import Stepper from './stepper.vue';
import GoogleSerpSimulator from '@/features/content-briefs/brief-content/GoogleSerpSimulator.vue';
import RecommendedContent from '@/features/content-briefs/brief-content/RecommendedContent.vue';
import helper from '@/services/Helper';
import BaseSortMixins from '@/common/BaseSortMixins';
import filters from '@/common/Filters';

export default {
    name: 'ContentBriefWizardStep3',
    props: {
        keywords: Array,
        url: String,
        title: String,
        description: String,
    },
    components: {
        Stepper,
        GoogleSerpSimulator,
        RecommendedContent
    },
    mixins: [BaseSortMixins],
    filters: { ...filters },
    data() {
        return {
            titleInner: this.title,
            descriptionInner: this.description,
            tableConfig: [
                {
                    label: 'Keyword',
                    propName: 'keyword'
                },
                {
                    label: 'Search Volume / Mo.',
                    propName: 'msv'
                },
                {
                    label: 'Keyword Difficulty',
                    propName: 'difficultyScore'
                }
            ],
            sortConfig: [{
                sortDirection: helper.DIRECTION_DESC,
                propertyName: 'msv'
            }],
        }
    },
    computed: {
        sortedKeywordsList() {
            if (!this.sortConfig.length) {
                return this.keywords
            }

            function sortData(data, param, direction) {
                helper.sortByParamMutation(data, param, direction)
                data.forEach(item => {
                    if (item.children && item.children.length) {
                        sortData(item.children, param, direction)
                    }
                })
            }
            const { propertyName, sortDirection } = this.sortConfig[0]
            const sortedData = _.cloneDeep(this.keywords)
            sortData(sortedData, propertyName, sortDirection)

            return sortedData
        },
    },
    methods: {
        next() {
            this.$emit('metadataSet', { title: this.titleInner, description: this.descriptionInner });
        },
        back() {
            this.$emit('previous')
        }
    },

}
</script>

<style scoped
    lang="scss">
    .cb-wizard-step3 {
        &__body {
            overflow-y: auto;
            height: calc(95vh - 240px);
        }

        &__serp {
            max-width: none;
            width: auto;
            margin-top: 0;
        }

        &__recommended {
            margin-top: 30px;
        }

        &__table {
            margin-top: 30px;
        }

        &__sortable-header {
            background: transparent;
            color: $rfx-dark-march;
            font-size: 14px;
            font-weight: 700;
        }

        .common-table {
            border: 1px solid $rfx-light-steel;

            &__header {
                background-color: rgba(1, 22, 39, .1);
                padding-right: 16px;
            }

            &__header-cell,
            &__cell {
                padding-top: 10px;
                padding-bottom: 10px;
            }
        }

        &__checkbox-cell {
            flex: 0 0 20px;
            padding-left: 20px;
            padding-right: 0;
        }

        &__keyword-cell {
            flex: 2 0 180px;
            padding-left: 20px;
            padding-right: 0;
        }

        &__msv-cell {
            flex: 2 0 150px;
        }

        &__difficultyScore-cell {
            flex: 1 0 120px;
        }
    }
</style>